/**
 * TranslationBlob Component
 *
 * Displays a translated paragraph as a colored blob positioned
 * on the right side of the PDF viewer.
 *
 * Features:
 * - Pastel background color with matching border
 * - Hover state with elevated shadow
 * - Smooth transitions for hover effects
 */

import React from "react";
import { TranslatedParagraph } from "./translationTypes.js";
import { getColorForIndex } from "./colorPalette.js";

interface TranslationBlobProps {
  /** The translation data to display */
  translation: TranslatedParagraph;
  /** Viewport-relative Y position (pixels) */
  top: number;
  /** X position - right edge of PDF plus margin (pixels) */
  marginX: number;
  /** Whether this blob is currently hovered */
  isHovered: boolean;
  /** Callback when hover state changes */
  onHover: (hovering: boolean) => void;
}

export function TranslationBlob({
  translation,
  top,
  marginX,
  isHovered,
  onHover,
}: TranslationBlobProps) {
  const colors = getColorForIndex(translation.colorIndex);

  return (
    <div
      className="translationBlob"
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      style={{
        position: "absolute",
        left: marginX,
        top: top,
        width: 280,
        maxWidth: "calc(100vw - 60px - 280px)", // Don't overflow viewport
        padding: "12px 16px",
        backgroundColor: colors.bg,
        borderLeft: `4px solid ${colors.border}`,
        borderRadius: "0 8px 8px 0",
        boxShadow: isHovered
          ? "0 4px 16px rgba(0, 0, 0, 0.12)"
          : "0 2px 8px rgba(0, 0, 0, 0.06)",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        transform: isHovered ? "translateX(4px)" : "none",
        cursor: "pointer",
        zIndex: isHovered ? 101 : 100,
        // Prevent text selection during scroll
        userSelect: "text",
      }}
    >
      <p
        style={{
          fontFamily: "Dr-Medium, Georgia, serif",
          fontSize: 14,
          lineHeight: 1.6,
          color: "rgba(0, 0, 0, 0.85)",
          margin: 0,
          // Text rendering
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        }}
      >
        {translation.translatedText}
      </p>
    </div>
  );
}

export default TranslationBlob;
